import {
  APIError,
  getResponseParseErrorSentryExtra,
  getShortRequestId,
  ResponseParseError,
} from "@certn/api";
import { useRequest } from "@certn/api/src/useRequest";
import { showToast } from "@certn/cascadia";
import * as Sentry from "@sentry/react";
import { FormikErrors, FormikHelpers, FormikValues } from "formik";
import { useTranslation } from "react-i18next";

const defaultOptions = {};

export function useFormSubmit(
  url: string,
  options: {
    onSuccess?: (values: FormikValues) => void;
    raiseError?: boolean;
  } = defaultOptions
) {
  const { t } = useTranslation();
  const request = useRequest();

  return async function <T extends object>(
    values: T,
    formikHelpers: FormikHelpers<T>
  ) {
    try {
      await request(url, {
        method: "POST",
        body: JSON.stringify(values),
      });
      options.onSuccess && options.onSuccess(values);
    } catch (e: any) {
      if (options.raiseError || e instanceof APIError)
        formikHelpers.setErrors(e.errors as FormikErrors<T>);

      if (options.raiseError) throw e; // requires consumer try catch

      if (e instanceof ResponseParseError) {
        const shortRef = getShortRequestId(e.requestId);
        showToast(
          shortRef
            ? t("Something went wrong, try again. Reference: {{ref}}", {
                ref: shortRef,
              })
            : t("Something went wrong, try again"),
          { type: "error" }
        );
        Sentry.captureException(e, {
          extra: getResponseParseErrorSentryExtra(e),
        });
      } else if (e instanceof APIError) {
        showToast(Array.isArray(e.detail) ? e.detail[0] : e.detail, {
          type: "error",
        });
      } else {
        showToast(t("Something went wrong, try again"), {
          type: "error",
        });
      }
    }
  };
}
